import type { Translations } from './types';

export const en: Translations = {
  nav: {
    dashboard: 'Dashboard',
    subscription: 'Subscription',
    usage: 'Usage',
    analytics: 'Analytics',
    settings: 'Settings',
  },
  languageToggle: {
    label: 'IT',
    accessibilityLabel: 'Switch to Italian',
  },
  dashboard: {
    title: 'Dashboard',
    welcomeTitle: 'Welcome to Aisthetix Virtual Try-On',
    welcomeBody:
      'Get started by selecting a subscription plan to enable virtual try-on for your store.',
    choosePlan: 'Choose a Plan',
    currentPlan: 'Current Plan',
    status: 'Status',
    unknown: 'Unknown',
    usageSummary: 'Usage Summary',
    tryOnsUsed: (used, total) => `${used} of ${total} try-ons used`,
    quotaExhausted: 'Monthly limit reached',
    quotaExhaustedBody:
      "You've used all your try-ons this month. Virtual try-on is paused for your customers until the next billing cycle or you upgrade your plan.",
    approachingLimit: (remaining) =>
      `You're approaching your monthly limit — only ${remaining} try-ons left.`,
    upgradeNow: 'Upgrade Plan',
    daysRemaining: (days) => `${days} days remaining in billing period`,
    noUsageData: 'No usage data available',
    quickActions: 'Quick Actions',
    manageSubscription: 'Manage Subscription',
    viewDetailedUsage: 'View Detailed Usage',
    billingPeriod: 'Billing Period',
    startDate: 'Start Date',
    endDate: 'End Date',
    trialProgress: (used, cap) => `Trial: ${used} of ${cap} try-ons used`,
    overageActive: 'Extra try-ons in use',
    overageActiveBody: (price, cap) =>
      `Your included try-ons are used up, so virtual try-on keeps running at ${price} per extra try-on. You will not be charged more than ${cap} this billing period.`,
    freePlanTitle: 'Free plan',
    freePlanBody: (count) =>
      `Your store gets ${count} free try-ons each month. Upgrade to keep virtual try-on running once they are used.`,
    freePlanExhausted: 'Free try-ons used',
    freePlanExhaustedBody: (count) =>
      `You have used all ${count} free try-ons this month. Virtual try-on is paused for your customers until next month, or until you choose a plan.`,
    sizeCoverageTitle: 'Some products cannot get a size recommendation',
    sizeCoverageGuideMismatch: (guideName) =>
      `The sizes in “${guideName}” match none of the products it is assigned to.`,
    sizeCoverageUncovered: (count) => `${count} recent product(s) are covered by no size guide.`,
    sizeCoverageAction: 'Review size guides',
    sizeCoverageCheckIncomplete: 'The last coverage check did not finish. Some guides may not be up to date yet.',
  },
  subscription: {
    title: 'Subscription Plans',
    cancelSubscription: 'Cancel Subscription',
    keepSubscription: 'Keep Subscription',
    cancelModalTitle: 'Cancel Subscription',
    cancelModalBody:
      'Are you sure you want to cancel your subscription? This will:',
    cancelModalList: [
      'Disable virtual try-on features for your store',
      'Stop all recurring charges',
      'Remove access to premium features',
    ],
    cancelModalReactivate:
      'You can reactivate your subscription at any time.',
    currentPlanBanner: (tier) =>
      `You are currently subscribed to the ${tier} plan.`,
    subscriptionActive: ' Your subscription is active.',
    subscriptionStatus: (status) => ` Status: ${status}`,
    subscriptionInactiveBanner: (tier, status) =>
      `Your previous ${tier} subscription is ${status.toLowerCase()}. Choose a plan below to start a new subscription.`,
    freeEnrolled: 'You are on the free plan',
    freeEnrolledBody: (count) =>
      `Your store gets ${count} try-ons each month at no cost. Choose a paid plan when you need more.`,
    overageExplainer:
      'Paid plans keep serving customers past the included try-ons, charged per extra try-on up to a cap you approve. You are never charged more than that cap.',
  },
  billingProfile: {
    title: 'Billing details',
    description:
      'Required for compliant invoices. Paid plans stay locked until the legal identity of your business is complete.',
    complete: 'Complete',
    required: 'Required',
    businessType: 'Business type',
    company: 'Company',
    soleTrader: 'Sole trader / individual business',
    legalNameCompany: 'Legal business name',
    legalNameSoleTrader: 'Full legal name',
    taxId: 'Business tax ID / VAT ID / EIN',
    addressLine1: 'Billing address',
    addressLine2: 'Address line 2',
    city: 'City',
    region: 'State / province / region',
    postalCode: 'Postal code',
    countryCode: 'Country code',
    countryHelp: 'Use the two-letter ISO code, for example US, IT or FR.',
    save: 'Save billing details',
    saving: 'Saving…',
    saved: 'Billing details saved. Paid plans are now available.',
    invalid: 'Complete every required field. Country must be a two-letter ISO code.',
    loadError: 'Could not load billing details.',
    saveError: 'Could not save billing details.',
    paidPlanBlocked: 'Complete and save your billing details before choosing a paid plan.',
  },
  usage: {
    title: 'Usage Statistics',
    noSubscriptionTitle: 'No Active Subscription',
    noSubscriptionBody:
      'You need an active subscription to view usage statistics. Choose a plan to get started.',
    viewPlans: 'View Plans',
    noUsageData:
      'No usage data available for your current billing period.',
    quotaExhausted: 'Monthly limit reached',
    quotaExhaustedBody:
      "You've used all your try-ons this month. Virtual try-on is paused for your customers until the next billing cycle or you upgrade your plan.",
    approachingLimit: (remaining) =>
      `Only ${remaining} try-ons remaining this month. Consider upgrading your plan.`,
    upgradeNow: 'Upgrade Plan',
    usageBreakdown: 'Usage Breakdown',
    billingPeriod: 'Billing Period',
    startDate: 'Start Date',
    endDate: 'End Date',
    daysRemaining: 'Days Remaining',
    days: (count) => `${count} days`,
    currentPlanDetails: 'Current Plan Details',
    plan: 'Plan',
    monthlyPrice: 'Monthly Price',
    metric: 'Metric',
    count: 'Count',
    includedTryOns: 'Included Try-Ons',
    usedTryOns: 'Used Try-Ons',
    remainingTryOns: 'Remaining Try-Ons',
    trialTitle: 'Free Trial',
    trialProgress: (used, cap) => `${used} of ${cap} trial try-ons used`,
    overageTitle: 'Extra Try-Ons',
    overageActiveBody: (price) =>
      `Your included try-ons are used up. Virtual try-on keeps running at ${price} per extra try-on, up to the cap you approved.`,
    overageTryOns: 'Extra Try-Ons Billed',
    overageSpend: 'Extra Try-On Charges',
    overageCap: 'Your Cap',
    freePlanTitle: 'Free Plan',
    freePlanBody: (count) =>
      `Your store gets ${count} try-ons each month at no cost. Choose a paid plan for more try-ons and to keep serving customers once the free ones are used.`,
  },
  analytics: {
    title: 'Aisthetix Effectiveness',
    subtitle: (days) => `Last ${days} days`,
    correlationalDisclaimer:
      'These figures compare shoppers who used Try-On or Find My Size with shoppers who did not. They describe correlation, not proof that Aisthetix caused a purchase.',
    funnelTitle: 'Shopper Funnel',
    funnelHelp: 'Distinct shoppers who reached each stage in the period.',
    stage: 'Stage',
    shoppers: 'Shoppers',
    shopperProductPairs: 'Shopper/product pairs',
    stageViewed: 'Viewed a product',
    stageTriedOn: 'Used try-on',
    stageSized: 'Used Find My Size',
    stageUsedAisthetix: 'Used Aisthetix',
    stageAddedToCart: 'Added to cart',
    stagePurchased: 'Purchased',
    usageTitle: 'Aisthetix usage',
    usageHelp: 'Distinct shopper/product pairs. Try-On only, Find My Size only and both are mutually exclusive.',
    tryOnOnly: 'Try-On only',
    findMySizeOnly: 'Find My Size only',
    bothFeatures: 'Both',
    usedAisthetix: 'Used Aisthetix total',
    comparisonTitle: 'Used Aisthetix vs Did not use Aisthetix',
    usedLabel: 'Used Aisthetix',
    didNotUseLabel: 'Did not use Aisthetix',
    comparisonResult: (points) => `${points >= 0 ? '+' : ''}${points.toFixed(1)} percentage points`,
    ratioUnavailable: 'A rate ratio is not defined because the comparison group had no purchases; the normalized rates remain shown.',
    liftTitle: 'Try-On Lift',
    liftHeadline: (multiplier) =>
      `Try-on shoppers bought the same product ${multiplier}× more often`,
    liftDetail: (tryOnPct, baselinePct) =>
      `${tryOnPct}% of try-on shoppers bought the product they tried on, vs ${baselinePct}% of shoppers who viewed it without trying on.`,
    liftCausalCaveat:
      'This is a correlation, not proof of cause — shoppers who try on may already be more likely to buy.',
    collectingData: (minShoppers) =>
      `Collecting data — we need at least ${minShoppers} shopper/product pairs in both groups before showing the comparison chart.`,
    revenueTitle: 'Attributed Revenue',
    attributedRevenue: 'Attributed revenue',
    attributedOrders: 'Attributed orders',
    revenueHelp:
      'Revenue from orders containing a product for which the shopper used Try-On or Find My Size, purchased within 30 days. Each order is counted once.',
    loadError: 'Could not load analytics. Please try again.',
  },

  reviewPrompt: {
    title: 'Try-on is bringing in revenue',
    body: 'Davide here, the person who builds Aisthetix. Your store now has its first sales attributed to virtual try-on. If Aisthetix is useful to you, a review on the Shopify App Store tells me what is working and what to focus on next. It takes less than a minute.',
    leaveReview: 'Leave a review',
    maybeLater: 'Maybe later',
    dismiss: 'Dismiss',
    unavailable:
      'The review form is not available right now. You can review Aisthetix from the Shopify App Store at any time.',
  },

  planCard: {
    active: 'Active',
    perMonth: 'per month',
    tryOnsIncluded: (count) => `${count} try-ons included`,
    features: 'Features:',
    currentPlan: 'Current Plan',
    upgrade: 'Upgrade',
    downgrade: 'Downgrade',
    subscribe: 'Subscribe',
    trialBadge: (days) => `${days}-day free trial`,
    chargedEvery: (days) => `Then charged every ${days} days`,
    freeBadge: 'Free forever',
    freePrice: '$0.00 USD',
    startFree: 'Start free',
    noCardRequired: 'No credit card required',
    trialCapNote: (cap) => `Trial includes up to ${cap} try-ons`,
    overageLine: (price) => `Then ${price} per extra try-on`,
    overageCapNote: (cap) => `Capped at ${cap} per billing period, and you set the cap`,
    freeStopsAtLimit: 'Try-on pauses once the free try-ons are used',
  },
  usageProgress: {
    title: 'Try-On Usage',
    used: (count) => `Used: ${count}`,
    included: (count) => `Included: ${count}`,
    remaining: (count) => `Remaining: ${count}`,
  },
  settings: {
    title: 'Settings',
    rateLimitingTitle: 'Per-User Rate Limiting',
    rateLimitingDescription:
      'Limit how many virtual try-ons each visitor can perform within a time window. This helps control usage and prevents abuse.',
    enableRateLimiting: 'Enable per-user rate limiting',
    maxGenerations: 'Maximum try-ons per visitor',
    maxGenerationsHelp: 'How many try-ons each visitor can perform before being temporarily blocked (1-100).',
    cooldownPeriod: 'Cooldown period',
    cooldownCustom: 'Custom cooldown duration',
    cooldownMinutes: 'minutes',
    exampleText: (max, cooldown) =>
      `Each visitor can try on up to ${max} items. After reaching the limit, they must wait ${cooldown} before trying again.`,
    save: 'Save settings',
    saveSuccess: 'Settings saved successfully',
    saveError: 'Failed to save settings',
    cooldown30min: '30 minutes',
    cooldown1hr: '1 hour',
    cooldown2hr: '2 hours',
    cooldown3hr: '3 hours',
    cooldown6hr: '6 hours',
    cooldown12hr: '12 hours',
    cooldown24hr: '24 hours',
    cooldownCustomLabel: 'Custom',
    fitPreferenceTitle: 'Default fit preference',
    fitPreferenceDescription:
      'Choose the default fit for size recommendations. Collection overrides below take precedence where they apply.',
    fitPreferenceLabel: 'Fit',
    fitPreferenceHelp: 'Fitted favours a closer size; relaxed favours a roomier size.',
    fitPreferenceTight: 'Fitted',
    fitPreferenceRegular: 'Regular',
    fitPreferenceLoose: 'Relaxed',
    fitOverridesTitle: 'Fit by collection',
    fitOverridesHelp: 'The global fit above applies to every collection without an override. For products in multiple overridden collections, the lowest priority number wins; ties use the collection ID.',
    fitOverridesAdd: 'Add collection override',
    fitOverridesEmpty: 'No overrides. The global fit applies everywhere.',
    fitOverridesPriority: (category) => `Priority for ${category}`,
    fitOverridesPreference: (category) => `Fit for ${category}`,
    fitOverridesRemove: (category) => `Remove ${category} override`,
    fitOverridesPickerUnavailable: 'Open this page from Shopify Admin to choose collections.',

    sizeGuidesTitle: 'Size guides',
    sizeGuidesDescription:
      'Upload one guide per product category. The size step uses the guide assigned to the product being tried on; without a usable guide, Find my size explains why a recommendation is unavailable.',
    sizeGuidesAddAction: 'Add a size guide',

    sizeGuideDropLabel: 'Size guide file',
    sizeGuideDropHint: 'PNG, JPEG, WebP or PDF, up to 10 MB.',
    sizeGuideDropAction: 'Add file',
    sizeGuideReplaceTitle: 'Replace the guide',
    sizeGuideRemove: 'Remove guide',
    sizeGuideRemoving: 'Removing...',
    sizeGuideUploading: 'Reading your guide...',
    sizeGuideRowCount: (rows) => (rows === 1 ? '1 size read from your guide' : `${rows} sizes read from your guide`),
    sizeGuideShowAll: (rows) => `Show all ${rows} sizes`,
    sizeGuideShowFewer: 'Show fewer',
    sizeGuideSizeColumn: 'Size',
    sizeGuideEmpty:
      'We could not read a single size out of this file. Check that the chart is legible and upload it again, or the size recommendation stays off.',
    sizeGuideParsedAt: (date) => `Read on ${date}`,
    sizeGuideSourceUnitCm: 'Read as centimetres',
    sizeGuideSourceUnitIn: 'Read as inches, converted to centimetres',
    sizeGuideBasisBody: 'Body measurements',
    sizeGuideBasisGarment: 'Garment measurements',
    sizeGuideBasisUnknown: 'Measurements of unknown type',
    sizeGuideBasisBlockedTitle: 'Size recommendations are off for this guide',
    sizeGuideBasisBlockedBody:
      'We compare your chart against the shopper body, so only a chart of body measurements can be used. Until you upload one, shoppers get the virtual try-on but no size recommendation.',
    sizeGuideBasisGarmentDetail:
      'This chart looks like garment measurements: the width of the clothes laid flat, not the body they fit. Used as body measurements it would recommend a size or two off, on every product, without looking wrong.',
    sizeGuideBasisUnknownDetail:
      'We could not tell whether this chart measures the body or the garment. Look for a chart that states it lists body measurements.',

    sizeGuideNameLabel: 'Guide name',
    sizeGuideNameSave: 'Save name',
    sizeGuideFallbackBadge: 'Default guide',
    sizeGuideFallbackHint: 'A guide with no categories applies to every product no other guide covers.',
    sizeGuideTypesLabel: 'Product types',
    sizeGuideTypesPlaceholder: 'e.g. Pants',
    sizeGuideCollectionsLabel: 'Collections',
    sizeGuideTagsLabel: 'Tags',
    sizeGuideTagsPlaceholder: 'Press Enter to add a tag',

    sizeGuideErrorUnreadable:
      'We could not extract this size guide reliably. The existing guides were not changed.',
    sizeGuideErrorInvalidFile: 'This file could not be opened as a valid image or PDF. Export it again and retry.',
    sizeGuideErrorImageUnreadable: 'This image could not be read. Export it again as PNG, JPEG or WebP.',
    sizeGuideErrorNoText: 'The file opened, but no readable text was found. Try a selectable-text PDF or a higher-contrast export.',
    sizeGuideErrorNoTable: 'The file is readable, but no size-table structure was detected.',
    sizeGuideErrorCells: 'A table was detected, but its cells could not be extracted reliably.',
    sizeGuideErrorNoRows: 'A table was detected, but it has no usable size rows and measurements.',
    sizeGuideAlternative: 'You can retry without losing your current setup. If it still fails, export a selectable-text PDF or contact support with the reference below.',
    sizeGuideErrorReference: (id) => `Reference: ${id}`,
    sizeGuideErrorParserDown:
      'The service that reads size charts is unavailable right now. Your settings are untouched. Try again in a few minutes.',
    sizeGuideErrorTooLarge: 'That file is over 10 MB. Try a smaller image or a shorter PDF.',
    sizeGuideErrorUnsupportedType: 'That file type is not supported. Use PNG, JPEG, WebP or PDF.',
    sizeGuideErrorGeneric: 'We could not upload that guide. Try again.',
    sizeGuideProductsLabel: "Specific products",
    sizeGuideProductsHelp: "Products take priority over other criteria. A product can belong to only one guide. Leave all criteria empty for a fallback guide.",
    sizeGuideProductsUnavailable: "Product unavailable. Retry or remove the reference.",
    sizeGuideProductPublished: "Published",
    sizeGuideProductUnpublished: "Not published / unavailable",
    sizeGuideErrorConflict: (name) => `One of those values is already assigned to “${name}”. Remove it there first.`,
    sizeGuideErrorSecondFallback: (name) => `“${name}” is already the default guide. Assign categories to one of the two.`,
    sizeGuideErrorTooMany: (max) => `You can keep at most ${max} size guides.`,
    sizeGuideErrorInvalidName: 'The guide name cannot be empty.',
    sizeGuideErrorSettingsChanged:
      'Your settings changed somewhere else while you were editing. Reload the page and try again.',

    sizeGuideCoverageOk: (matched, sampled) => `Sizes match ${matched} of ${sampled} products checked.`,
    sizeGuideCoverageMismatch: (matched, sampled) =>
      `Sizes match only ${matched} of ${sampled} products checked. The size step will not appear on the others. Compare the guide labels with those products’ size options.`,
    sizeGuideCoverageExamples: (titles) => `For example: ${titles}`,
    sizeGuideCoverageUnchecked: 'Coverage not checked yet.',
    sizeGuideCoverageFailed: 'Coverage could not be checked. It will be retried automatically.',
    sizeCoverageCheckedAt: (date) => `Coverage last checked on ${date}`,

    measurements: {
      height_cm: 'Height (cm)',
      chest_cm: 'Chest (cm)',
      underbust_cm: 'Underbust (cm)',
      waist_cm: 'Waist (cm)',
      hip_cm: 'Hip (cm)',
      inseam_cm: 'Inseam (cm)',
      shoulder_width_cm: 'Shoulder width (cm)',
      sleeve_length_cm: 'Sleeve length (cm)',
      neck_cm: 'Neck (cm)',
      thigh_cm: 'Thigh (cm)',
      foot_length_cm: 'Foot length (cm)',
    },

    targetingTitle: 'Where the try-on and the size appear',
    targetingDescription:
      'Two independent rules. Products a rule leaves out never show that feature and never spend a try-on.',
    targetingTryonTitle: 'Virtual try-on',
    targetingTryonDescription: 'Where the try-on button appears on your storefront.',
    targetingSizeTitle: 'Size recommendation',
    targetingSizeDescription:
      'Where shoppers are offered a size. Keep this to the products your size guide really covers.',
    targetingModeLabel: 'Show on',
    targetingModeAll: 'All products',
    targetingModeInclude: 'Only the products I choose',
    targetingModeExclude: 'All products except the ones I choose',
    targetingCollections: 'Collections',
    targetingProducts: 'Products',
    targetingChooseCollections: 'Choose collections',
    targetingChooseProducts: 'Choose products',
    targetingNoCollections: 'No collections chosen.',
    targetingNoProducts: 'No products chosen.',
    targetingProductFallbackLabel: (id) => `Product ${id}`,
    targetingPickerUnavailable:
      'Open this page from your Shopify admin to choose collections and products.',
    targetingPickerFailed: 'We could not add everything you chose. Nothing was changed. Try again.',
    targetingCoverageAll: 'Shown on every product in your store.',
    targetingCoverageIncludeEmpty:
      'Nothing is chosen yet, so this is shown on no product at all. Choose at least one collection or product.',
    targetingCoverageInclude: (collections, products) =>
      `Shown only on products in the ${collections} collections and on the ${products} products chosen here.`,
    targetingCoverageExcludeEmpty: 'Nothing is excluded, so this is shown on every product.',
    targetingCoverageExclude: (collections, products) =>
      `Hidden on products in the ${collections} collections and on the ${products} products chosen here, shown everywhere else.`,
    targetingCoverageCaveat:
      'How many products that adds up to depends on your catalogue, so we do not show a count here.',
    targetingLimitReached: (max) => `You can choose up to ${max} entries per list.`,
    targetingSyncPendingTitle: 'Your storefront has not caught up yet',
    targetingSyncPendingBody:
      'Your settings are saved, but the copy your storefront reads could not be updated. Until it is, a product page may still show a button you switched off. Save again to retry.',
    targetingSyncDetail: (reason) => `Shopify said: ${reason}`,
    targetingSyncedAt: (date) => `Storefront updated on ${date}`,
  },

  onboarding: {
    title: 'Set Up Virtual Try-On',
    subtitle: 'Follow these steps to get your virtual try-on widget live on your store.',
    step1Title: '1. Check Theme Compatibility',
    step1Body: 'Virtual try-on requires an Online Store 2.0 theme. We\'ll check this automatically.',
    step2Title: '2. Choose a Plan',
    step2Body: 'Select a subscription plan that fits your store\'s needs.',
    step2Action: 'Choose a Plan',
    step3Title: '3. Add the Widget to Your Theme',
    step3Body: 'Go to your theme editor, navigate to a product page template, and add the "Virtual Try-On" app block. You can choose between the full widget or button-only block.',
    step3Action: 'Open Theme Editor',
    step4Title: '4. Configure Appearance',
    step4Body: 'Customize the button color, text color, and per-user rate limiting from the Settings page.',
    step4Action: 'Go to Settings',
    step5Title: '5. Upload your size guide',
    step5Body:
      'Upload your size chart so shoppers can be told which size to order. It has to be a chart of body measurements: we show you the table we read, so you can check it before it goes live.',
    step5Action: 'Upload a size guide',
    step5Done: 'Size guide uploaded',
    step5NotBodyBasis:
      'Your guide is uploaded, but it was not read as body measurements, so size recommendations stay off. Open Settings to see what we read.',
    step6Title: '6. Choose where try-on and size appear',
    step6Body:
      'By default both run on every product. Narrow them down to the products they suit: anything left out never shows the feature and never spends a try-on.',
    step6Action: 'Choose products',
    watchVideo: 'Watch tutorial video',
    dismiss: 'Dismiss',
    showSetupGuide: 'Show setup guide',
  },

  onboardingFlow: {
    dismiss: 'Skip for now',
    stepOf: (current, total) => `Step ${current} of ${total}`,
    productTitle: 'See it on you — with your own product',
    productSubtitle:
      'We picked a product from your store to try on. You can switch to any other product.',
    productContinue: 'Use this product',
    productChange: 'Choose another product',
    productPickerTitle: 'Choose a product',
    productPickerClose: 'Close',
    productRiskyBadge: 'May be declined by moderation',
    productNoImageBadge: 'No image',
    productRiskyOnlyWarning:
      'The products we found are in categories the try-on engine often declines (swimwear, underwear). You can still pick one, but the first result may be refused.',
    productEmptyTitle: 'No products found',
    productEmptyBody:
      'Your store has no active products with an image yet. Add a product in Shopify, then come back — the demo needs a real product to try on.',
    productEmptyRefresh: 'Check again',
    productLoadFailed: 'We could not load your products. Please try again.',
    photoTitle: 'Add your photo',
    photoSubtitle: 'A quick photo of yourself is all the demo needs.',
    photoGuide1: 'Full body, standing, facing the camera',
    photoGuide2: 'Good light, plain background if you can',
    photoGuide3: 'Just you in the frame',
    photoPrivacy:
      'Your photo is used only to generate this try-on. It is never stored on our servers and never leaves this generation.',
    photoSelect: 'Choose a photo',
    photoReplace: 'Use a different photo',
    photoConfirm: 'Generate my try-on',
    photoPreviewAlt: 'Preview of your photo',
    photoInvalidType: 'Please choose a JPEG, PNG or WebP image.',
    photoTooLarge: 'That photo is too large. Please choose one under 20 MB.',
    photoReadError: 'We could not read that photo. Please try another one.',
    photoBack: 'Back to product',
    genStagePhoto: 'Preparing your photo…',
    genStageProduct: (productTitle) => `Analysing ${productTitle}…`,
    genStageCreate: 'Creating your try-on…',
    genHint: 'This usually takes about ten seconds.',
    resultTitle: 'Here it is — on you',
    resultSubtitle: (productTitle) => `${productTitle}, from your store, on your photo.`,
    resultAlt: 'Your virtual try-on result',
    resultOriginalAlt: 'Your original photo',
    resultShowOriginal: 'Show my photo',
    resultShowTryOn: 'Show the try-on',
    resultRetryPhoto: 'Try another photo',
    resultRetryProduct: 'Try another product',
    resultCta: 'Put it live on my store',
    resultRemaining: (remaining) =>
      remaining === 1 ? '1 demo try-on left' : `${remaining} demo try-ons left`,
    demoErrorTitle: 'That one did not work',
    demoErrorFlagged:
      'The image provider declined this product — it happens with swimwear and similar categories. Your photo is fine.',
    demoErrorCannotGenerate:
      'The engine could not generate a try-on for this product. Another product will usually work.',
    demoErrorInvalidPhoto: 'We could not use that photo. Please try a different one.',
    demoErrorPayloadTooLarge: 'That photo is too large to process. Please choose a smaller one.',
    demoErrorImageUnavailable:
      'We could not load this product’s image. Try another product, or check the image in Shopify.',
    demoErrorEngineUnavailable:
      'The try-on service is temporarily unavailable. Please try again in a moment.',
    demoErrorTimeout: 'This took longer than expected. Please try again.',
    demoErrorAllowanceExhausted:
      'You have used all the demo try-ons for this store. The widget on your storefront is not affected.',
    demoErrorTooSoon: 'A try-on is already running. Give it a few seconds.',
    demoErrorProductNotFound:
      'That product is no longer available for the demo. Please pick another one.',
    demoErrorUnexpected: 'Something went wrong on our side. Please try again.',
    demoKeepPhoto: 'Your photo is still here — no need to upload it again.',
    demoTryNextProduct: (productTitle) => `Retry with “${productTitle}”`,
    demoRetry: 'Try again',
    simTitle: 'What Aisthetix will measure for you',
    simSubtitle:
      'Once the widget is live, every step below becomes real data in this dashboard. Here is how it will look, using your product.',
    simDisclaimer: 'Demo simulation — this is not real data and not a forecast.',
    simStepTryon: 'Virtual try-on completed',
    simStepCart: 'Added to cart',
    simStepOrder: 'Attributed order',
    simStepRevenue: 'Attributed revenue',
    simRevenueNote: 'Shown with this product’s actual price.',
    simReplay: 'Replay',
    simCta: 'Start collecting real data',
    simBack: 'Back to my try-on',
    actTitle: 'One step left: put the widget on your store',
    actBody:
      'The theme editor will open with the Aisthetix block already added to your product page. Review where it sits, then press Save.',
    actManualNote:
      'Shopify requires you to press Save yourself — that is the only step we cannot do for you.',
    actOpenEditor: 'Open theme editor',
    actAutoCheckNote: 'We detect the save automatically — come back to this tab when done.',
    actManualCheck: 'I saved — check now',
    actChecking: 'Checking your theme…',
    actDisabledOnly:
      'The block is in your theme but currently disabled. Enable it in the theme editor and save.',
    actVerifiedTitle: 'The widget is live',
    actVerifiedBody: (themeName) => `We found the Aisthetix block in “${themeName}”. You are done.`,
    actDone: 'Go to my dashboard',
    actBack: 'Back',
  },

  progressiveDashboard: {
    activationPendingTitle: 'Your widget is not on your storefront yet',
    activationPendingBody:
      'Shoppers cannot see the try-on until the app block is saved in your theme. It takes one minute.',
    activationPendingAction: 'Open theme editor',
    activationCheck: 'Check again',
    waitingTitle: 'Waiting for your first shopper',
    waitingBody:
      'The widget is live. As soon as a shopper tries something on, real numbers replace this card.',
    replaySimulation: 'Replay the example',
    viewMyTryOn: 'See my try-on',
    myTryOnTitle: 'Your try-on',
    firstTryonTitle: 'Your store served its first try-on 🎉',
    firstTryonBody: 'Shoppers are using it. Here is what has happened so far.',
    tryonsServed: (count) => (count === 1 ? '1 try-on served' : `${count} try-ons served`),
    cartsTitle: 'Add-to-carts after a try-on',
    ordersTitle: 'Attributed orders',
    revenueTitle: 'Attributed revenue',
    lockedUntilData: 'Unlocks with your first data',
    viewAnalytics: 'Open analytics',
    tryDemoTitle: 'Try the new dashboard demo',
    tryDemoBody:
      'See a virtual try-on generated with your own product and photo, right here in the dashboard.',
    tryDemoAction: 'Try it on me',
  },

  themeCompatibility: {
    checking: 'Checking theme compatibility…',
    compatible: (themeName) => `Your theme "${themeName}" supports virtual try-on.`,
    incompatibleTitle: 'Incompatible theme detected',
    incompatibleBody: (themeName) =>
      `Your current theme${themeName ? ` "${themeName}"` : ''} does not support app blocks (Online Store 2.0 required). Please switch to a compatible theme before subscribing.`,
    incompatibleSubscribeWarning:
      'Your current theme does not support app blocks. Please switch to an Online Store 2.0 theme before subscribing.',
  },

  // Same two moments, same promises as the emails and as the WooCommerce side.
  trial: {
    lowTitle: 'Your trial is running low',
    lowBody: (remaining, cap) =>
      `You have used ${cap - remaining} of your ${cap} trial try-ons. When they run out, the try-on button is hidden from your store until your plan starts.`,
    exhaustedTitle: 'Trial try-ons used',
    exhaustedBody: (cap, trialEndsOn) =>
      `You have used all ${cap} trial try-ons, so the try-on button is hidden from your store. Start your plan to switch it back on, or wait: it begins automatically on ${trialEndsOn}.`,
    startPlan: (planName) => `Start ${planName} now`,
    approvalNote: 'Shopify will ask you to approve the charge. Nothing is charged until you do.',
    failed: 'We could not start your plan. Please try again.',
  },
};
